import { Auth } from 'aws-amplify';
import axios, { AxiosResponse, AxiosRequestConfig } from 'axios';
import axiosRetry from 'axios-retry';

interface IApiHelper extends AxiosRequestConfig {
    refresh?: boolean,
    retries?: number
}

axiosRetry(axios, { retries: 3, retryDelay: axiosRetry.exponentialDelay });

class ApiHelper {

    private static getHeaders = async (isIncludeToken:boolean, contentType: string = 'application/json') => {
        try {
            let headers = {
                Authorization: '',
                Accept: '*/*',
                "Content-Type": contentType
            };
           
            if(isIncludeToken) {
                const session = await Auth.currentSession();
                const token = session.getIdToken().getJwtToken()
                headers["Authorization"] = `Bearer ${token}`;
            }
            return headers;
        } catch (err) {
            console.error('Cannot get current session, proceding without authorization token');
            return {
                Accept: '*/*',
                "Content-Type": contentType
            };
        }
    }

    private static buildAxiosConfig = async (isAuthorizationRequired:boolean, config?: IApiHelper, contentType?: string): Promise<AxiosRequestConfig | undefined> => {

        // always add auth token
        config = {
            ...config, headers: {
                ...await ApiHelper.getHeaders(isAuthorizationRequired, contentType)
            }
        };

        if (config.refresh) {
            config.headers = {
                ...config.headers,
                'Cache-Control': 'no-cache'
            };
        }

        if (config.retries) {
            config['axios-retry'] = {
                retries: config.retries
            }
        }

        return config;
    }

    public static get = async (url: string, isAuthorizationRequired:boolean, config?: IApiHelper): Promise<AxiosResponse<any>> => {
        return axios.get(url, await ApiHelper.buildAxiosConfig(isAuthorizationRequired, config));
    }

    public static getPhoto = async (url: string, isAuthorizationRequired:boolean, config?: IApiHelper): Promise<AxiosResponse<any>> => {
        config = { ...config, responseType: 'arraybuffer' }
        return axios.get(url, await ApiHelper.buildAxiosConfig(isAuthorizationRequired, config));
    }

    public static post = async (url: string, isAuthorizationRequired:boolean, data?: any, config?: IApiHelper, contentType?: string): Promise<AxiosResponse<any>> => {
        return axios.post(url, data, await ApiHelper.buildAxiosConfig(isAuthorizationRequired, config, contentType));
    }

    public static put = async (url: string, isAuthorizationRequired:boolean, data?: any, config?: IApiHelper): Promise<AxiosResponse<any>> => {
        return axios.put(url, data, await ApiHelper.buildAxiosConfig(isAuthorizationRequired, config));
    }

    public static patch = async (url: string, isAuthorizationRequired:boolean, data?: any, config?: IApiHelper): Promise<AxiosResponse<any>> => {
        return axios.patch(url, data, await ApiHelper.buildAxiosConfig(isAuthorizationRequired, config));
    }

    public static delete = async (url: string, isAuthorizationRequired:boolean, config?: IApiHelper): Promise<AxiosResponse<any>> => {
        return axios.delete(url, await ApiHelper.buildAxiosConfig(isAuthorizationRequired, config));
    }
}

export default ApiHelper;